import type { ContentNode, DocumentState } from '../document-types';
import type { NodeHandler, RenderContext } from './types';
import type { DocumentHandlerContext } from '../document-handler.type';
import { EraQL } from '@cotera/era';
import {
  extractSymbolReferencesFromEraql,
  extractColumnReferencesFromEraql,
} from '../extract-symbol-references';

export class ExpressionHandler implements NodeHandler {
  readonly type = 'expression';
  readonly escapeBackticks = false;

  /**
   *
   * @param node
   * @param _parent
   * @param _childContent
   * @param ctx
   * @returns The markdown string - if mode is expression, return templated expression, if mode is simple, return evaluated expression
   */
  render(
    node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    _childContent: string,
    ctx: RenderContext
  ): string {
    const attrs = (node as ContentNode).attrs;
    const rawEraql = attrs?.['eraql'];

    // Ensure eraql is a valid non-empty string before parsing
    if (rawEraql == null) {
      return '';
    }

    const eraql = typeof rawEraql === 'string' ? rawEraql : String(rawEraql);
    if (eraql.trim().length === 0) {
      return '';
    }

    if (ctx.mode === 'expression') {
      //return templated expression
      return `{{ ${eraql} }}`;
    }

    // Parse the expression to validate it
    // Wrap parseExpr in try-catch to handle any errors
    let parseResult;
    try {
      parseResult = ctx.graph.parser.parseExpr(eraql);
    } catch (error) {
      // If parsing throws an error, show the expression with an error indicator
      return `\`\`\`eraql\n${eraql}\n\`\`\`\n\n*Error: Failed to parse expression*\n\n`;
    }

    // Format the expression in a code block
    // If parsing failed, still show the expression but indicate it's invalid
    if (parseResult.isErr()) {
      // Show the expression with an error indicator
      const errors = parseResult.error
        .map(([err]) => EraQL.prettyPrintParseError(err))
        .join(', ');
      return `\`\`\`eraql\n${eraql}\n\`\`\`\n\n*Error: ${errors}*\n\n`;
    }

    //if mode is simple, return evaluated expression
    // Expression is valid, show it in a code block
    return `\`\`\`\n${parseResult.value.evaluate() ?? ''}\n\`\`\`\n\n`;
  }

  getDependencies(node: ContentNode, _ctx: DocumentHandlerContext): string[] {
    const eraql = node.attrs?.['eraql'] as string | undefined;
    if (eraql) {
      // Extract both symbol references (%symbol) and column references ("column" or column)
      const symbolRefs = extractSymbolReferencesFromEraql(eraql);
      const columnRefs = extractColumnReferencesFromEraql(eraql);
      // Combine and deduplicate
      return Array.from(new Set([...symbolRefs, ...columnRefs]));
    }
    return [];
  }

  replaceReference(
    node: ContentNode,
    oldReference: string,
    newReference: string
  ): ContentNode | null {
    const eraql = node.attrs?.['eraql'] as string | undefined;
    if (!eraql) {
      return null;
    }

    const updatedEraql = EraQL.renameSymbolInEraql({
      from: oldReference,
      to: newReference,
      eraql: eraql,
    });

    if (updatedEraql !== eraql) {
      return {
        ...node,
        attrs: {
          ...node.attrs,
          eraql: updatedEraql,
        },
      };
    }

    return node;
  }
}
